import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import ToolManager from './ToolManager';
import ProgramManager from './ProgramManager';
import { getHmiDefByTypeCode } from '../services/simDefApi';
import { createActionBridge } from './hmi/core/HmiActionBridge';
import FanucScreen from './hmi/FanucScreen';
import { useFocasAdapter } from './hmi/system/useFocasAdapter';
import SystemScreen from './hmi/system/SystemScreen';
import AdapterScreen from './hmi/system/AdapterScreen';
import DiagnosticsScreen from './hmi/system/DiagnosticsScreen';
import { clearAlarm } from '../services/alarmApi';
import { machineApi } from '../services/api';

const API_BASE = '/machinesim/api';
const SOCKET_PATH = '/machinesim/socket.io';

/**
 * Per-machine operator HMI — a declarative FANUC CNC screen.
 *
 * Fetches the field metadata for the machine type and renders {@link FanucScreen}
 * from it (widget choice = field.hmiResolved.widget). Live state comes over the
 * socket; the SAME FanucScreen renders the Studio preview in `previewMode`.
 *
 * @param {object} props
 * @param {{ name: string, state: object }} props.machine
 * @param {string} [props.typeCode]
 * @param {string} [props.company]  company slug — scopes the FOCAS adapter poll
 * @param {boolean} [props.previewMode]
 * @param {object | null} [props.hmiPayloadOverride]  { machineType, fields } for Studio preview
 * @param {import('react').MutableRefObject<(() => Promise<boolean>) | null | undefined>} [props.mesEstopUserConfirmRef]
 */
function DynamicHmiPanel({
  machine,
  typeCode,
  company = 'default',
  initialPage = null,
  previewMode = false,
  hmiPayloadOverride = null,
  mesEstopUserConfirmRef = null,
}) {
  const [hmiPayload, setHmiPayload] = useState(() =>
    hmiPayloadOverride ? { ...hmiPayloadOverride } : null
  );
  const [hmiError, setHmiError] = useState(null);
  const [liveState, setLiveState] = useState(() => ({ ...machine.state }));
  const [availablePrograms, setAvailablePrograms] = useState({});
  const [showToolManager, setShowToolManager] = useState(false);
  const [showProgramManager, setShowProgramManager] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(false);

  // Field keys with an in-flight debounced write — don't let a 1s server push
  // clobber a value the operator is actively dragging.
  const pendingRef = useRef(new Set());
  const socketRef = useRef(null);

  const code = typeCode || import.meta.env.VITE_DEFAULT_MACHINE_TYPE || 'CNC_LATHE';

  useEffect(() => {
    if (hmiPayloadOverride) {
      setHmiPayload({ ...hmiPayloadOverride });
      setHmiError(null);
      return;
    }
    getHmiDefByTypeCode(code)
      .then((data) => {
        setHmiPayload(data);
        setHmiError(null);
      })
      .catch((err) => {
        console.error('HMI def load failed:', err);
        setHmiError(err.response?.data?.error || err.message || 'Failed to load HMI definition');
      });
  }, [code, hmiPayloadOverride]);

  useEffect(() => {
    if (previewMode) return;
    axios
      .get(`${API_BASE}/programs`)
      .then((res) => setAvailablePrograms(res.data || {}))
      .catch(() => {});
  }, [previewMode]);

  const updateStateFromServer = useCallback((state) => {
    setLiveState((prev) => {
      const next = { ...prev, ...state };
      // Preserve fields with an in-flight local edit.
      for (const key of pendingRef.current) {
        if (state[key] !== undefined) next[key] = prev[key];
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setLiveState({ ...machine.state });
  }, [machine]);

  useEffect(() => {
    if (previewMode) return;
    const socket = io({
      path: SOCKET_PATH,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;
    socket.on('connect', () => {
      setIsSocketConnected(true);
      socket.emit('subscribeMachine', machine.name);
    });
    socket.on('disconnect', () => setIsSocketConnected(false));
    socket.on('connect_error', () => setIsSocketConnected(false));
    socket.on('machineUpdate', (data) => {
      if (data.name === machine.name && data.state) {
        updateStateFromServer(data.state);
      }
    });
    return () => {
      socket.emit('unsubscribeMachine', machine.name);
      socket.disconnect();
    };
  }, [previewMode, machine.name, updateStateFromServer]);

  useEffect(() => {
    if (previewMode || isSocketConnected) return;
    const fetchMachineStatus = async () => {
      try {
        const res = await axios.get(`${API_BASE}/${encodeURIComponent(machine.name)}/status`);
        updateStateFromServer(res.data);
      } catch (err) {
        console.error('DynamicHmi status poll:', err);
      }
    };
    fetchMachineStatus();
    const interval = setInterval(fetchMachineStatus, 1000);
    return () => clearInterval(interval);
  }, [previewMode, machine.name, isSocketConnected, updateStateFromServer]);

  const bridge = useMemo(
    () =>
      createActionBridge({
        previewMode,
        machineName: machine.name,
        setLiveState,
        pendingRef,
        mesEstopUserConfirmRef,
      }),
    [previewMode, machine.name, mesEstopUserConfirmRef]
  );

  // Cancel any in-flight debounced writes when the bridge is replaced or the
  // panel unmounts (e.g. navigating away mid-drag) so no stray write/setState fires.
  useEffect(() => () => bridge.dispose(), [bridge]);

  // Real-adapter poll feeding the engineer soft-key bank (ADAPTER + DGNOS).
  // Disabled in the Studio preview — that path shows the operator screen only.
  const adapter = useFocasAdapter(machine.name, company, !previewMode);

  if (hmiError) {
    return (
      <div className="max-w-xl mx-auto p-8 rounded-lg border border-red-800 bg-red-950/40 text-red-200">
        {hmiError}
      </div>
    );
  }

  if (!hmiPayload) {
    return (
      <div className="flex justify-center p-12 text-gray-400">
        <div className="animate-pulse">Loading HMI…</div>
      </div>
    );
  }

  // Engineer soft-key bank (FANUC continuous-menu ">"): FOCAS addressing config,
  // adapter view, and drift diagnostics — the phosphor-CRT successor to the old
  // toggled gray FOCAS panels. Preview shows the operator screen only.
  const systemScreens = previewMode
    ? null
    : {
        SYSTEM: <SystemScreen machine={machine} state={liveState} />,
        ADAPTER: <AdapterScreen adapter={adapter} />,
        DGNOS: <DiagnosticsScreen adapter={adapter} />,
      };

  return (
    <div className="flex flex-col items-center w-full">
      <FanucScreen
        machineType={hmiPayload.machineType}
        fields={hmiPayload.fields}
        values={liveState}
        bridge={bridge}
        availablePrograms={availablePrograms}
        previewMode={previewMode}
        onOpenToolManager={previewMode ? undefined : () => setShowToolManager(true)}
        onOpenProgramManager={previewMode ? undefined : () => setShowProgramManager(true)}
        systemScreens={systemScreens}
        // The MDI RESET key. Wired to the same endpoint the tablet pendant's Clear uses, rather than
        // left on the bridge where it would have been a silently dead key on a panel whose whole
        // point is that the live keys are live.
        onReset={previewMode ? undefined : () => clearAlarm(machine.name, company).catch(() => {})}
        initialPage={initialPage}
        // Mode switches go to the SAME endpoints the rest of the app uses, so the lamp on the cap
        // reflects the control and not a local toggle that drifts from it.
        onSetMode={previewMode ? undefined : (k, v) => {
          setLiveState((st) => ({ ...st, [k]: v }));
          const call = k === 'blockDelete'
            ? machineApi.setBlockDelete(machine.name, v)
            : machineApi.setCycleMode(machine.name, v);
          Promise.resolve(call).catch(() => {});
        }}
      />
      {showToolManager && !previewMode && (
        <ToolManager machineName={machine.name} onClose={() => setShowToolManager(false)} />
      )}
      {showProgramManager && !previewMode && (
        <ProgramManager onClose={() => setShowProgramManager(false)} />
      )}
    </div>
  );
}

export default DynamicHmiPanel;
